import {
  getNodeAtPath,
  getNodeParentPath
} from 'components/scenarios/utils/graph/nodes/getters'
import {
  isContinuousStatusCheckNode,
  isGraphNode
} from 'components/scenarios/utils/graph/nodes/typeguards'

import type {
  GraphNodeAtPath,
  GraphNodePath
} from 'components/scenarios/utils/graph/types'
import type { ScenarioGraph } from 'types/api/scenarios/scenario'

function replaceTopLevelGraphNodeAtPath(
  graph: ScenarioGraph,
  node: GraphNodeAtPath,
  nodePath: GraphNodePath
) {
  const newNodeKey = String(nodePath[1])

  if (newNodeKey) {
    if (nodePath[0] === 'observers' && isContinuousStatusCheckNode(node)) {
      if (!graph.observers) {
        graph.observers = [node]
      } else {
        graph.observers[newNodeKey] = node
      }
    } else if (nodePath[0] === 'nodes' && isGraphNode(node)) {
      if (!graph.nodes) {
        graph.nodes = { [newNodeKey]: node }
      } else {
        graph.nodes[newNodeKey] = node
      }
    }
  }

  return graph
}

export function replaceGraphNodeAtPath(
  graph: ScenarioGraph,
  node: GraphNodeAtPath,
  nodePath: GraphNodePath
): ScenarioGraph {
  if (!node) {
    return graph
  }

  // short circuit top level replacement
  if (nodePath.length === 1) {
    graph[nodePath[0]] = node
    return graph
  }

  // short circuit top-level graph paths that have no parent
  if (nodePath.length < 3) {
    return replaceTopLevelGraphNodeAtPath(graph, node, nodePath)
  }

  const parentPath = getNodeParentPath(nodePath)
  const parent = getNodeAtPath(graph, parentPath)

  if (parent) {
    const key = String(nodePath.at(-1))

    if (key) {
      parent[key] = node
    }
  }

  return graph
}

function deleteTopLevelGraphNodeAtPath(
  graph: ScenarioGraph,
  nodePath: GraphNodePath
) {
  const nodeKey = String(nodePath[1])

  if (nodeKey) {
    if (nodePath[0] === 'observers') {
      graph.observers?.splice(Number(nodeKey), 1)
    } else if (nodePath[0] === 'nodes') {
      delete graph.nodes[nodeKey]
    }
  }

  return graph
}

/**
 * Delete a node from the graph at the supplied path
 */
export function deleteGraphNodeAtPath(
  graph: ScenarioGraph,
  nodePath: GraphNodePath
): ScenarioGraph {
  // short circuit top-level graph paths that have no parent
  if (nodePath.length < 3) {
    return deleteTopLevelGraphNodeAtPath(graph, nodePath)
  }

  const parentPath = getNodeParentPath(nodePath)
  const parent = getNodeAtPath(graph, parentPath)

  if (parent) {
    const key = String(nodePath.at(-1))

    if (key) {
      if (Array.isArray(parent)) {
        parent.splice(Number(key), 1)
      } else {
        delete parent[key]
      }
    }
  }

  return graph
}
